import { useEffect, useRef, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

import { OtpCodeInput } from './OtpCodeInput';

interface ConfirmCodeStepProps {
  /** Email address the code was sent to — shown in the body copy. */
  readonly email: string;
  /** Called with the entered code when the user submits. */
  readonly onSubmit: (code: string) => Promise<void>;
  /** Called when the user clicks "Resend code". The parent handles the SDK call. */
  readonly onResend: () => Promise<void>;
  /** Inline error message from the wizard (code mismatch, expired, etc.). */
  readonly topError?: string | null;
}

const RESEND_COOLDOWN_SECONDS = 30;
const CODE_LENGTH = 6;

export function ConfirmCodeStep({ email, onSubmit, onResend, topError }: ConfirmCodeStepProps) {
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resentToastMessage, setResentToastMessage] = useState<string | null>(null);

  // We auto-submit once the user reaches six digits. This ref guards
  // against re-submitting the same code if the user backspaces a digit
  // then re-types it: we only auto-submit the FIRST time a given full
  // code is seen. A cleared input resets it so the same code can
  // auto-submit again after correction.
  const autoSubmittedCodeRef = useRef<string | null>(null);

  // Tick the resend cooldown down to zero.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => {
      setResendCooldown((c) => Math.max(0, c - 1));
    }, 1000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [resendCooldown]);

  // Clear the "code resent" confirmation after a few seconds.
  useEffect(() => {
    if (!resentToastMessage) return;
    const timer = window.setTimeout(() => {
      setResentToastMessage(null);
    }, 4000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [resentToastMessage]);

  const submit = async (codeToSubmit: string) => {
    if (codeToSubmit.length !== CODE_LENGTH || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit(codeToSubmit);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (next: string) => {
    setCode(next);
    if (next.length === CODE_LENGTH && autoSubmittedCodeRef.current !== next) {
      autoSubmittedCodeRef.current = next;
      void submit(next);
    } else if (next.length < CODE_LENGTH) {
      autoSubmittedCodeRef.current = null;
    }
  };

  const resend = async () => {
    // No early-return on cooldown — the Resend button below is `disabled`
    // whenever `resendCooldown > 0`, and React's synthetic onClick never
    // fires for a disabled button. A duplicate guard here would be
    // unreachable; if you remove the disabled prop, add a new guard
    // appropriate to the new surface.
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    await onResend();
    setResentToastMessage(`Code sent to ${email}.`);
  };

  const canSubmit = code.length === CODE_LENGTH && !isSubmitting;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-foreground text-base font-semibold">Check your email</h3>
        <p className="text-muted-foreground text-sm">
          We sent a {CODE_LENGTH}-digit code to{' '}
          <span className="text-foreground font-medium">{email}</span>. Enter it below to confirm
          your account.
        </p>
      </div>

      {topError ? (
        <Alert variant="destructive">
          <AlertDescription>{topError}</AlertDescription>
        </Alert>
      ) : null}

      {resentToastMessage ? (
        <Alert>
          <AlertDescription>{resentToastMessage}</AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="confirm-code-0">Verification code</Label>
        <OtpCodeInput
          id="confirm-code-0"
          value={code}
          onChange={handleChange}
          describedBy="confirm-code-hint"
        />
        <p id="confirm-code-hint" className="text-muted-foreground text-xs">
          The code expires in 24 hours. Check your spam folder if it doesn&apos;t arrive within a
          minute.
        </p>
      </div>

      <Button
        type="button"
        className="w-full"
        disabled={!canSubmit}
        onClick={() => void submit(code)}
      >
        {isSubmitting ? 'Confirming…' : 'Confirm and continue'}
      </Button>

      <p className="text-muted-foreground text-center text-xs">
        Didn&apos;t get a code?{' '}
        <button
          type="button"
          onClick={() => void resend()}
          disabled={resendCooldown > 0}
          className="text-primary hover:underline disabled:no-underline disabled:opacity-60"
        >
          {resendCooldown > 0 ? `Resend in ${String(resendCooldown)}s` : 'Resend code'}
        </button>
      </p>
    </div>
  );
}
